import "./LodgeCollateralDialog.scss";
import {CollateralService} from "../service/CollateralService";
import {IOnInit} from "angular";
import {EquityCollateral, UnlodgedCollateral} from "../../../../types/PortfolioMarginResult";
import {
    CollateralMovement,
    CollateralMovementType,
    LodgeCollateralRequest,
    PortfolioCollateral,
    UnlodgeCollateralRequest
} from "../models/PortfolioCollateral";
import {UnlodgedTableRow} from "../models/table/UnlodgedTableRow";
import {LodgedCollateralTableRow} from "../models/table/LodgedCollateralTableRow";

export class LodgeCollateralDialogController implements IOnInit {

    public hasCollateral = false;

    public collateral: PortfolioCollateral;
    public unlodged: Array<UnlodgedTableRow>;
    public lodgedSpecific: Array<LodgedCollateralTableRow>;
    public lodgedGeneral: Array<LodgedCollateralTableRow>;

    /** @ngInject */
    public constructor(private collateralService: CollateralService,
                       private portfolioId: number) {
    }

    public $onInit() {
        this.updateCurrentCollateral();
    }

    private async updateCurrentCollateral() {
        const collateral = await this.collateralService.collateralForPortfolio(this.portfolioId);
        this.collateral = collateral;
        const pending = collateral.pending;

        const pendingUnlodges = pending.filter(p => p.type === CollateralMovementType.UNLODGE);
        const pendingSpecific = pending.filter(p => p.type === CollateralMovementType.SPECIFIC);
        const pendingGeneral = pending.filter(p => p.type === CollateralMovementType.GENERAL);

        this.unlodged = this.unlodgedRows(collateral.unlodgedCollateral, pending)
                            .sort((a, b) => a.code.localeCompare(b.code));

        this.lodgedSpecific = this.lodgedRows(collateral.specificCover, pendingSpecific, pendingUnlodges)
                                  .sort((a, b) => a.code.localeCompare(b.code));

        this.lodgedGeneral = this.lodgedRows(collateral.generalCover, pendingGeneral, pendingUnlodges)
                                 .sort((a, b) => a.code.localeCompare(b.code));

        this.hasCollateral = (this.unlodged.length + this.lodgedSpecific.length + this.lodgedGeneral.length) > 0;
    }

    private unlodgedRows(unlodged: Array<UnlodgedCollateral>,
                         pendings: Array<CollateralMovement>): Array<UnlodgedTableRow> {
        const unlodgedCodes = unlodged.map(ul => ul.securityCode);
        const pendingCodes = pendings.map(p => p.code);
        const allCodes = unlodgedCodes.concat(pendingCodes);
        const distinctCodes = Array.from(new Set(allCodes));

        return distinctCodes.map(code => {
            const collateral = unlodged.find(ul => ul.securityCode === code);
            const pendingUnlodge = pendings.filter(p => p.type === CollateralMovementType.UNLODGE)
                                           .find(p => p.code === code);
            const pendingLodge = pendings.filter(p => p.type !== CollateralMovementType.UNLODGE)
                                         .find(p => p.code === code);
            return new UnlodgedTableRow(collateral, pendingUnlodge, pendingLodge);
        });

    }

    private lodgedRows(lodgedCollateral: Array<EquityCollateral>,
                       pendingsLodges: Array<CollateralMovement>,
                       pendingUnlodges: Array<CollateralMovement>): Array<LodgedCollateralTableRow> {
        const unlodgedCodes = lodgedCollateral.map(l => l.issuer);
        const pendingLodgeCodes = pendingsLodges.map(p => p.code);
        const pendingUnlodgeCodes = pendingUnlodges.map(p => p.code);
        const allCodes = unlodgedCodes.concat(pendingLodgeCodes).concat(pendingUnlodgeCodes);
        const distinctCodes = Array.from(new Set(allCodes));

        return distinctCodes.map(code => {
            const collateral = lodgedCollateral.find(l => l.issuer === code);
            const pendingLodge = pendingsLodges.find(p => p.code === code);
            const pendingUnlodge = pendingUnlodges.find(p => p.code === code);
            return new LodgedCollateralTableRow(collateral, pendingLodge, pendingUnlodge);
        });
    }

    public async lodgeSpecific(code: string, quantity: number) {
        const request: LodgeCollateralRequest = {
            code,
            quantity,
            specific: true
        };
        await this.collateralService.lodge(this.portfolioId, request);
        this.updateCurrentCollateral();
    }

    public async lodgeGeneral(code: string, quantity: number) {
        const request: LodgeCollateralRequest = {
            code,
            quantity,
            specific: false
        };
        await this.collateralService.lodge(this.portfolioId, request);
        this.updateCurrentCollateral();
    }

    public async unlodge(code: string, quantity: number) {
        const request: UnlodgeCollateralRequest = {
            code,
            quantity
        };
        await this.collateralService.unlodge(this.portfolioId, request);
        this.updateCurrentCollateral();
    }
}
